import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Step, StepMap } from '@atlaskit/editor-prosemirror/transform';
import { ReplaceAroundStep, ReplaceStep } from '@atlaskit/editor-prosemirror/transform';

import type { AIMonitorWatchmenPluginState } from './ai-monitor-watchmen-plugin-key';
import type { CountOverrides, PendingAnalytics, Session, TrackedRange } from './types';

export const mergeRanges = (ranges: Array<{ from: number; to: number }>) => {
	if (!ranges.length) {
		return ranges;
	}
	const sorted = [...ranges].sort((a, b) => a.from - b.from);
	const out: Array<{ from: number; to: number }> = [];
	let cur = { ...sorted[0] };
	for (let i = 1; i < sorted.length; i++) {
		const r = sorted[i];
		if (r.from <= cur.to) {
			cur.to = Math.max(cur.to, r.to);
		} else {
			out.push(cur);
			cur = { ...r };
		}
	}
	out.push(cur);
	return out;
};

const TEXT_BETWEEN_BLOCK = ' ';
const TEXT_BETWEEN_LEAF = ' ';
export const textBetween = (doc: PMNode, from: number, to: number): string => {
	return doc.textBetween(from, to, TEXT_BETWEEN_BLOCK, TEXT_BETWEEN_LEAF);
};

const wordSegmenter =
	typeof Intl !== 'undefined' && Intl.Segmenter
		? new Intl.Segmenter(undefined, { granularity: 'word' })
		: null;

export const countWords = (text: string): number => {
	const t = text.trim();
	if (!t) {
		return 0;
	}
	try {
		if (wordSegmenter) {
			let c = 0;
			for (const { isWordLike } of wordSegmenter.segment(t)) {
				if (isWordLike) {
					c++;
				}
			}
			return c;
		}
	} catch {}
	// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
	return t.split(/\s+/u).filter(Boolean).length;
};

export const sumTextCounts = (doc: PMNode, ranges: Array<{ from: number; to: number }>) => {
	let chars = 0;
	let words = 0;
	for (const r of ranges) {
		const s = textBetween(doc, r.from, r.to);
		chars += s.length;
		words += countWords(s);
	}
	return { chars, words };
};

export const collectChangedRangesFromMapping = (maps: readonly StepMap[]): TrackedRange[] => {
	const out: TrackedRange[] = [];
	for (const m of maps) {
		m.forEach((oldStart, oldEnd, newStart, newEnd) => {
			if (oldStart !== oldEnd || newStart !== newEnd) {
				out.push({ oldStart, oldEnd, newStart, newEnd });
			}
		});
	}
	return out;
};

export const isExactInverseOfSession = (
	maps: readonly StepMap[],
	sessionRanges: TrackedRange[],
): boolean => {
	if (!sessionRanges.length) {
		return false;
	}
	const key = (a: number, b: number, c: number, d: number) => `${a}:${b}:${c}:${d}`;
	const need = new Set(sessionRanges.map((r) => key(r.newStart, r.newEnd, r.oldStart, r.oldEnd)));
	for (const m of maps) {
		m.forEach((os, oe, ns, ne) => need.delete(key(os, oe, ns, ne)));
	}
	return need.size === 0;
};

export const isRedoOfUndoneSession = (
	maps: readonly StepMap[],
	undoneRanges: TrackedRange[],
): boolean => {
	if (!undoneRanges.length) {
		return false;
	}
	// A redo is the inverse of an undo, so we check if the transaction
	// restores the exact content that was undone
	const key = (a: number, b: number, c: number, d: number) => `${a}:${b}:${c}:${d}`;
	const need = new Set(undoneRanges.map((r) => key(r.oldStart, r.oldEnd, r.newStart, r.newEnd)));
	for (const m of maps) {
		m.forEach((os, oe, ns, ne) => need.delete(key(os, oe, ns, ne)));
	}
	return need.size === 0;
};

export const countSessionReductions = (
	steps: Step[],
	stepMaps: readonly StepMap[],
	baselineDoc: PMNode,
	sessionRangesAfterAI: Array<{ from: number; to: number }>,
): { deletedChars: number; deletedWords: number; touched: number } => {
	let localDoc = baselineDoc;
	let currentRanges = sessionRangesAfterAI.map((r) => ({ ...r }));
	let deletedChars = 0;
	let deletedWords = 0;
	const touchedIdx = new Set<number>();

	for (let i = 0; i < steps.length; i++) {
		const step = steps[i];

		let delFrom: number | null = null;
		let delTo: number | null = null;

		if (step instanceof ReplaceStep) {
			if (typeof step.from === 'number' && typeof step.to === 'number' && step.from < step.to) {
				delFrom = step.from;
				delTo = step.to;
			}
		} else if (step instanceof ReplaceAroundStep) {
			const ra = step as ReplaceAroundStep;
			const deletedSpans = [
				{ from: ra.from as number, to: ra.gapFrom as number },
				{ from: ra.gapTo as number, to: ra.to as number },
			].filter((s) => s.from < s.to);

			for (const span of deletedSpans) {
				for (let idx = 0; idx < currentRanges.length; idx++) {
					const r = currentRanges[idx];
					const start = Math.max(r.from, span.from);
					const end = Math.min(r.to, span.to);
					if (start < end) {
						touchedIdx.add(idx);
						const removed = textBetween(localDoc, start, end);
						deletedChars += removed.length;
						deletedWords += countWords(removed);
					}
				}
			}
		}
		if (delFrom != null && delTo != null) {
			for (let idx = 0; idx < currentRanges.length; idx++) {
				const r = currentRanges[idx];
				const start = Math.max(r.from, delFrom);
				const end = Math.min(r.to, delTo);
				if (start < end) {
					touchedIdx.add(idx);
					const removed = textBetween(localDoc, start, end);
					deletedChars += removed.length;
					deletedWords += countWords(removed);
				}
			}
		}

		const result = step.apply(localDoc);
		if (result.doc) {
			localDoc = result.doc;
		}

		const map = stepMaps[i];
		if (map) {
			currentRanges = currentRanges.map(({ from, to }) => ({
				from: map.map(from, -1),
				to: map.map(to, 1),
			}));
		}
	}

	return { deletedChars, deletedWords, touched: touchedIdx.size };
};

export const finishImmediateReaction = (
	state: AIMonitorWatchmenPluginState,
	s: Session,
	type: PendingAnalytics['nextActionType'],
	counts: CountOverrides = {},
): AIMonitorWatchmenPluginState => {
	const now = performance.now();
	const { removedChars = undefined, removedWords = undefined, touched = undefined } = counts;

	return {
		...state,
		currentSession: null,
		pendingAnalytics: {
			aiSessionId: s.id,
			nextActionType: type,
			aiOutputChars: s.aiOutputChars,
			aiOutputWords: s.aiOutputWords,
			nextActionRemovedAiChars: removedChars,
			nextActionRemovedAiWords: removedWords,
			nextActionTouchedAiRanges: touched,
			nextActionLatencyMs: Math.round(Math.max(0, now - s.startedAt)),
			aiGroupId: s.aiGroupId,
			experienceName: s.experienceName,
		},
	};
};

export const txnTouchesAiRanges = (
	maps: readonly StepMap[],
	aiRanges: ReadonlyArray<{ from: number; to: number }>,
): boolean => {
	const changed = collectChangedRangesFromMapping(maps);
	for (let i = 0; i < changed.length; i++) {
		const c = changed[i];
		for (let j = 0; j < aiRanges.length; j++) {
			const r = aiRanges[j];
			if (c.oldEnd > r.from && c.oldStart < r.to) {
				return true;
			}
		}
	}
	return false;
};
